const MAYACHITRO_STORY_BASE =
  "/uploads/public/stories/mayachitro/photo-stories";

const CLOUDY_PIXEL_STORY_BASE =
  "/uploads/public/stories/cloudy-pixel-studio/wall-art-journal";

const photoStoryPosts = [
  {
    id: "story-001",
    slug: "golden-river-tree-line-sunset-photography-story",
    type: "Photo Story",
    category: "Photo Stories",
    brand: "MayaChitro",

    title: "Golden River Tree Line Sunset Photography: A Quiet Bangladesh Evening",

    seoTitle:
      "Golden River Tree Line Sunset Photography Story | Bangladesh Sunset Wall Art | MayaChitro",

    metaDescription:
      "Read the story behind a peaceful Bangladesh sunset photograph featuring golden river water, tree silhouettes, and warm wall art mood from MayaChitro.",

    excerpt:
      "A calm sunset moment over a Bangladesh river, where the glowing sun, dark tree line, and soft water texture create a peaceful wall art story.",

    date: "May 2026",
    readingTime: "4 min read",

    storyFolder: "MC-0016-golden-river-tree-line-sunset-photography",

    image:
      `${MAYACHITRO_STORY_BASE}/MC-0016-golden-river-tree-line-sunset-photography/golden-river-tree-line-sunset-photography-mayachitro.jpg`,

    thumbnail:
      `${MAYACHITRO_STORY_BASE}/MC-0016-golden-river-tree-line-sunset-photography/golden-river-tree-line-sunset-photography-mayachitro.jpg`,

    inlineImage:
      `${MAYACHITRO_STORY_BASE}/MC-0016-golden-river-tree-line-sunset-photography/mockup-04-premium-living-room-wall-art.png`,

    imageAlt:
      "Golden sunset over a Bangladesh river with dark tree line silhouette and warm water texture",

    productSlug: "golden-river-tree-line-sunset-photography",
    productBrandSlug: "mayachitro",
    productCategory: "Landscape Nature",
    productCta: "View This Photo",

    tags: [
      "Bangladesh sunset",
      "River photography",
      "Wall art",
      "Photo download",
      "Golden hour",
      "MayaChitro",
    ],

    intro:
      "Some photographs do not need a crowded frame. This MayaChitro photo is built around silence, warm light, and the quiet rhythm of river water. The glowing sun sits above a dark tree line, while the foreground carries soft golden reflections that make the image feel calm and timeless.",

    sections: [
      {
        heading: "The Story Behind the Frame",
        body:
          "The scene feels simple at first, but that simplicity is the strength of the photograph. The sky carries a muted orange tone, the trees become a natural silhouette, and the river creates a textured foreground. Together, these elements make the frame peaceful, balanced, and emotionally warm.",
      },
      {
        heading: "Why It Works as Wall Art",
        body:
          "This image is suitable for buyers who prefer calm, minimal, and warm interior moods. It can work beautifully in a bedroom, study room, living room, meditation corner, office wall, or gallery wall. The dark tree line gives structure, while the glowing sun adds a natural focal point.",
      },
      {
        heading: "Creative and Editorial Use",
        body:
          "Beyond wall art, this photograph can support blog posts, editorial stories, website visuals, social media designs, mood boards, and visual storytelling projects. The image has no identifiable person or visible logo, making it flexible for multiple license types depending on the selected usage option.",
      },
    ],

    buyingGuide:
      "Choose the personal digital download for home printing and personal decor. Choose an editorial or commercial license if you want to use the image for publication, websites, marketing, or business-related creative work.",

    faqs: [
      {
        q: "Can I print this sunset photo?",
        a: "Yes. The personal digital download is suitable for personal wall art and home decor printing.",
      },
      {
        q: "Can I use this photo commercially?",
        a: "Commercial use depends on the selected license. Choose the commercial option for business, marketing, website, or campaign use.",
      },
      {
        q: "Is this an instant download?",
        a: "Yes. After checkout, the purchased digital file is delivered through the download system.",
      },
    ],
  },

  {
    id: "story-002",
    slug: "solitary-boat-golden-river-sunset-photo-story",
    type: "Photo Story",
    category: "Behind the Frame",
    brand: "MayaChitro",

    title: "Solitary Boat on Golden River Water: The Story Behind the Frame",

    seoTitle:
      "Solitary Boat Golden River Sunset Photo Story | Bangladesh River Wall Art | MayaChitro",

    metaDescription:
      "Discover the story behind a peaceful MayaChitro river boat photograph with golden water, minimal composition, and warm Bangladesh sunset mood.",

    excerpt:
      "A single boat floating across golden water creates a peaceful visual story about silence, movement, and rural river life.",

    date: "May 2026",
    readingTime: "4 min read",

    storyFolder: "MC-0015-solitary-boat-golden-river-sunset-photography",

    image:
      `${MAYACHITRO_STORY_BASE}/MC-0015-solitary-boat-golden-river-sunset-photography/solitary-boat-golden-river-sunset-photography-mayachitro.jpg`,

    thumbnail:
      `${MAYACHITRO_STORY_BASE}/MC-0015-solitary-boat-golden-river-sunset-photography/solitary-boat-golden-river-sunset-photography-mayachitro.jpg`,

    inlineImage:
      `${MAYACHITRO_STORY_BASE}/MC-0015-solitary-boat-golden-river-sunset-photography/mockup-04-premium-living-room-wall-art.png`,

    imageAlt:
      "A solitary boat on golden river water during sunset in Bangladesh",

    productSlug: "solitary-boat-golden-river-sunset-photography",
    productBrandSlug: "mayachitro",
    productCategory: "Landscape Nature",
    productCta: "View Boat Photo",

    tags: [
      "Boat photography",
      "River life",
      "Bangladesh wall art",
      "Golden water",
      "Sunset print",
      "Photo story",
    ],

    intro:
      "This photograph is about space, silence, and direction. The boat moves through a wide river surface, surrounded by warm golden tones. There is no heavy distraction, only a quiet river moment that invites the viewer to slow down.",

    sections: [
      {
        heading: "A Minimal River Story",
        body:
          "The large empty water area gives the photograph breathing room. The boat becomes the emotional center, while the golden water creates a painterly backdrop. This kind of composition is powerful for wall art because it feels calm even from a distance.",
      },
      {
        heading: "Best Interior Mood",
        body:
          "This image fits warm neutral interiors, modern living rooms, study spaces, hotel decor, and calm gallery walls. It can be paired with other river, sunset, or nature photographs for a cohesive collection.",
      },
      {
        heading: "Useful for Storytelling Projects",
        body:
          "The image can also work for editorial writing, travel features, cultural storytelling, poetry visuals, and digital design projects where the theme is solitude, movement, river life, or evening calm.",
      },
    ],

    buyingGuide:
      "Use the personal license for home decor and personal printing. Select editorial or commercial licensing if the photo will appear in a publication, website, campaign, or business project.",

    faqs: [
      {
        q: "Is this suitable for large wall art?",
        a: "Yes. The minimal composition and warm color palette make it suitable for framed wall art.",
      },
      {
        q: "Can it be used for a blog or article?",
        a: "Yes, choose the appropriate editorial license for article or storytelling use.",
      },
      {
        q: "Does the photo include a model release?",
        a: "Check the product page license notes before commercial use if a recognizable person appears.",
      },
    ],
  },

  {
    id: "story-003",
    slug: "tiny-jumping-spider-green-leaf-macro-photo-story",
    type: "Photo Story",
    category: "Photo Stories",
    brand: "MayaChitro",

    title: "Tiny Jumping Spider on Green Leaf: A Macro Photography Story",

    seoTitle:
      "Tiny Jumping Spider Green Leaf Macro Photography Story | Nature Wall Art | MayaChitro",

    metaDescription:
      "Read the MayaChitro story behind a tiny jumping spider macro photograph on a green leaf, perfect for nature wall art and creative photo downloads.",

    excerpt:
      "A small spider on a green leaf becomes a detailed nature story through macro photography, texture, focus, and soft natural color.",

    date: "May 2026",
    readingTime: "3 min read",

    storyFolder: "MC-0007-tiny-jumping-spider-green-leaf-macro-photography",

    image:
      `${MAYACHITRO_STORY_BASE}/MC-0007-tiny-jumping-spider-green-leaf-macro-photography/tiny-jumping-spider-green-leaf-macro-photography-mayachitro.jpg`,

    thumbnail:
      `${MAYACHITRO_STORY_BASE}/MC-0007-tiny-jumping-spider-green-leaf-macro-photography/tiny-jumping-spider-green-leaf-macro-photography-mayachitro.jpg`,

    inlineImage:
      `${MAYACHITRO_STORY_BASE}/MC-0007-tiny-jumping-spider-green-leaf-macro-photography/mockup-01-living-room-console.png`,

    imageAlt:
      "Tiny jumping spider on green leaf macro photography by MayaChitro",

    productSlug: "tiny-jumping-spider-green-leaf-macro-photography",
    productBrandSlug: "mayachitro",
    productCategory: "Macro Details",
    productCta: "View Macro Photo",

    tags: [
      "Macro photography",
      "Jumping spider",
      "Green leaf",
      "Nature detail",
      "Wall art",
      "Photo download",
    ],

    intro:
      "Macro photography reveals a world that is usually missed. This tiny jumping spider on a green leaf feels almost like a character in a quiet natural scene. The shallow focus, green tones, and small subject make the photo detailed but still gentle.",

    sections: [
      {
        heading: "The Beauty of Small Details",
        body:
          "The spider is small, but the photograph gives it presence. The soft green background creates a clean mood, while the subject adds curiosity and life. It is a strong image for people who love nature, macro details, and unusual wall art.",
      },
      {
        heading: "Why It Attracts Nature Lovers",
        body:
          "Many buyers look for nature art that feels different from typical landscape prints. This macro image gives a close look at a living detail and can work well in study rooms, creative offices, classrooms, nature corners, and gallery walls.",
      },
      {
        heading: "Creative Use Ideas",
        body:
          "The image can support educational content, nature articles, biology-themed visuals, environmental storytelling, and design projects that need a fresh organic detail.",
      },
    ],

    buyingGuide:
      "Personal download is suitable for decor and personal projects. For educational, editorial, or commercial use, choose the correct license from the product page.",

    faqs: [
      {
        q: "Is this image suitable for nature decor?",
        a: "Yes. It works well for nature-inspired rooms, offices, classrooms, and calm gallery walls.",
      },
      {
        q: "Can I use it in educational materials?",
        a: "Yes, but choose the correct editorial or commercial license depending on how the material will be distributed.",
      },
      {
        q: "Will the paid image have a watermark?",
        a: "No. The paid download file should not include the website preview watermark.",
      },
    ],
  },

  {
    id: "story-004",
    slug: "bougainvillea-urban-bloom-botanical-photo-story",
    type: "Wall Art Idea",
    category: "Wall Art Journal",
    brand: "MayaChitro",

    title: "Bougainvillea Urban Bloom: Botanical Photography for Warm Wall Art",

    seoTitle:
      "Bougainvillea Urban Bloom Botanical Photography Story | Floral Wall Art | MayaChitro",

    metaDescription:
      "Explore the story behind MayaChitro bougainvillea botanical photography, a warm floral wall art image for homes, offices, and creative projects.",

    excerpt:
      "A soft botanical photograph of bougainvillea blooms brings color, warmth, and gentle urban nature into wall art and visual projects.",

    date: "May 2026",
    readingTime: "3 min read",

    storyFolder: "MC-0005-bougainvillea-bloom-urban-architecture",

    image:
      `${MAYACHITRO_STORY_BASE}/MC-0005-bougainvillea-bloom-urban-architecture/bougainvillea-bloom-urban-architecture-bokeh-photography-mayachitro.jpg`,

    thumbnail:
      `${MAYACHITRO_STORY_BASE}/MC-0005-bougainvillea-bloom-urban-architecture/bougainvillea-bloom-urban-architecture-bokeh-photography-mayachitro.jpg`,

    inlineImage:
      `${MAYACHITRO_STORY_BASE}/MC-0005-bougainvillea-bloom-urban-architecture/mockup-02-living-room-console.png`,

    imageAlt:
      "Pink bougainvillea bloom botanical photography with urban background",

    productSlug: "bougainvillea-bloom-urban-architecture",
    productBrandSlug: "mayachitro",
    productCategory: "Flowers Botanical",
    productCta: "View Botanical Photo",

    tags: [
      "Bougainvillea",
      "Floral wall art",
      "Botanical photography",
      "Urban bloom",
      "Pink flower",
      "MayaChitro",
    ],

    intro:
      "Flowers can turn an ordinary urban corner into something memorable. This bougainvillea photograph captures soft pink blooms against an architectural background, creating a mix of nature, color, and city life.",

    sections: [
      {
        heading: "Nature Inside the City",
        body:
          "The photograph is not a studio flower image. It carries the feeling of an outdoor urban space, where flowers soften the hard edges of buildings and walls. That contrast makes the image visually rich.",
      },
      {
        heading: "How It Works as Decor",
        body:
          "The pink and green tones can bring warmth to neutral interiors. It can work in bedrooms, hallways, creative studios, cafes, and gallery wall sets where a soft botanical accent is needed.",
      },
      {
        heading: "Best Uses for Buyers",
        body:
          "This image is suitable for personal wall art, blog headers, mood boards, floral design inspiration, editorial content, and creative projects that need a natural but urban visual style.",
      },
    ],

    buyingGuide:
      "Use personal download for personal prints and decor. Choose editorial or commercial license for publication, business branding, or design use.",

    faqs: [
      {
        q: "Is this good for printable floral wall art?",
        a: "Yes. Its soft color and botanical subject make it suitable for floral wall art.",
      },
      {
        q: "Can it be used as a website image?",
        a: "Yes, choose a commercial license for business or website use.",
      },
      {
        q: "Does the photo include a logo or trademark?",
        a: "No visible logo or trademark is the main subject of the image.",
      },
    ],
  },

  {
    id: "story-005",
    slug: "rainy-japanese-teahouse-alley-poster-wall-art-journal",
    type: "Wall Art Journal",
    category: "Wall Art Journal",
    brand: "Cloudy Pixel Studio",

    title: "Rainy Teahouse Alley Poster: Cozy Wall Art for Moody Interiors",

    seoTitle:
      "Rainy Teahouse Alley Poster Wall Art Journal | Moody Printable Wall Art | Cloudy Pixel Studio",

    metaDescription:
      "Discover how a rainy teahouse alley poster creates cozy, cinematic, and moody interior decor for printable wall art buyers.",

    excerpt:
      "A rainy alley scene with warm glowing lights creates a cozy wall art mood for bedrooms, offices, cafes, and gallery walls.",

    date: "May 2026",
    readingTime: "3 min read",

    storyFolder: "cps-0001-rainy-japanese-teahouse-alley",

    image:
      `${CLOUDY_PIXEL_STORY_BASE}/cps-0001-rainy-japanese-teahouse-alley/cover.png`,

    thumbnail:
      `${CLOUDY_PIXEL_STORY_BASE}/cps-0001-rainy-japanese-teahouse-alley/cover.png`,

    inlineImage:
      `${CLOUDY_PIXEL_STORY_BASE}/cps-0001-rainy-japanese-teahouse-alley/mockup-living-room.png`,

    imageAlt:
      "Rainy Japanese teahouse alley printable wall art poster by Cloudy Pixel Studio",

    productSlug: "rainy-japanese-teahouse-alley",
    productBrandSlug: "cloudy-pixel-studio",
    productCategory: "Rainy Street Posters",
    productCta: "View Wall Art",

    tags: [
      "Rainy wall art",
      "Cozy poster",
      "Printable wall art",
      "Moody decor",
      "Cloudy Pixel Studio",
      "Digital poster",
    ],

    intro:
      "Some wall art is designed to feel like a quiet evening. A rainy alley, soft glowing lights, and a calm street mood can make a room feel warmer and more cinematic. This poster concept is made for buyers who love cozy, atmospheric interiors.",

    sections: [
      {
        heading: "Why Rainy Wall Art Feels Cozy",
        body:
          "Rainy scenes naturally create mood. Reflections, warm light, and soft shadows can make a space feel peaceful and intimate. That is why rainy street posters often work well in bedrooms, reading corners, cafes, and creative workspaces.",
      },
      {
        heading: "Best Interior Pairings",
        body:
          "This type of wall art pairs well with dark wood, warm lamps, neutral bedding, bookshelves, beige walls, black frames, and cozy gallery wall arrangements.",
      },
      {
        heading: "Digital Poster Benefits",
        body:
          "Printable wall art is convenient because buyers can download the file, print it in their preferred size, and style it with a frame that matches their room.",
      },
    ],

    buyingGuide:
      "Choose this type of wall art if your room needs warmth, mood, and a story-like visual focal point.",

    faqs: [
      {
        q: "Is this a physical product?",
        a: "No. This is intended as a digital printable wall art product.",
      },
      {
        q: "Can I print it at home?",
        a: "Yes, depending on your printer quality and desired size. Professional printing is recommended for premium results.",
      },
      {
        q: "Can I sell the printed version?",
        a: "No. Reselling or redistributing the artwork is not included unless a separate commercial agreement is approved.",
      },
    ],
  },
];

// Expand the journal into a discoverable long-tail archive while new field stories are added.
const storyThemes = [
  ["River Light and Quiet Water", "river photography", "river life"],
  ["Golden Hour in Bangladesh", "sunset photography", "golden hour"],
  ["Small Details in the Natural World", "macro photography", "nature details"],
  ["A Closer Look at Everyday Life", "documentary photography", "visual story"],
  ["Photographs for Calm Interiors", "wall art photography", "interior styling"],
  ["Wildlife Moments Between Seasons", "wildlife photography", "birds and nature"],
  ["Colour, Texture, and Place", "travel photography", "creative reference"],
  ["Street Scenes with a Human Rhythm", "street photography", "city stories"],
  ["Landscape Stories from the Field", "landscape photography", "Bangladesh landscape"],
];

const generatedStories = Array.from({ length: 45 }, (_, index) => {
  const source = photoStoryPosts[index % photoStoryPosts.length];
  const theme = storyThemes[index % storyThemes.length];
  const number = String(index + 6).padStart(3, "0");
  const title = `${theme[0]}: ${source.title.split(":")[0]}`;
  return {
    ...source,
    id: `story-${number}`,
    slug: `${theme[1].replace(/\s+/g, "-")}-${number}`,
    title,
    seoTitle: `${title} | MayaChitro Photography Journal`,
    metaDescription: `Explore ${theme[1]} through a MayaChitro field note about ${theme[2]}, visual storytelling, and thoughtful image use.`,
    excerpt: `A MayaChitro journal note about ${theme[2]}, photographic observation, and finding images with meaning for homes and creative projects.`,
    category: theme[1],
    date: "MayaChitro Journal",
    readingTime: `${3 + (index % 3)} min read`,
    tags: [theme[1], theme[2], "MayaChitro", "photo story"],
  };
});

photoStoryPosts.push(...generatedStories);

export default photoStoryPosts;
